/**
 * FederationClerk · SFUI-002 storefront greeter.
 *
 * Rex as the Core's resident concierge. Floating bottom-right avatar +
 * speech bubble + quick-action buttons. Dismissible to a tiny dot.
 * Persistent across pages (mount in App.js root).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { REX_FACE_URL, REX_ALT } from "@/lib/personaFaces";

const REX_IMG = REX_FACE_URL;

const QUICK_ACTIONS = [
  { label: "🧠 Verify Something",   route: "/execute",            test: "clerk-verify"   },
  { label: "📚 Browse the Library", route: "/library",            test: "clerk-library"  },
  { label: "💬 Talk to a Mind",     route: "/chat",               test: "clerk-talk"     },
  { label: "🎯 Ring of Six",        route: "/deliberations",      test: "clerk-ring"     },
  { label: "🛒 Get a Token ($5)",   route: "/redeem",             test: "clerk-token"    },
];

const SATELLITE_LINKS = [
  { label: "🎨 Oracle · Chat",      route: "/chat?persona=oracle"   },
  { label: "✨ Aria (.studio)",      route: "https://nextxus.studio" },
  { label: "🛒 Store (.com)",        route: "https://next-xus.com"   },
  { label: "🎓 University (.help)",  route: "https://nextxus.help"   },
];

const COLLAPSED_KEY = "nx_clerk_collapsed_v1";

export default function FederationClerk() {
  const navigate = useNavigate();
  // Architect mandate Feb-2026: NEVER auto-open. The clerk is a clickable
  // dot at rest. Only expands when the user explicitly taps it. Once expanded
  // and dismissed, stays collapsed forever (localStorage key).
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem(COLLAPSED_KEY);
      // Default to collapsed (true) unless the user has explicitly opened it.
      return v === null ? true : v === "1";
    } catch { return true; }
  });
  const [showSatellites, setShowSatellites] = useState(false);

  // Skip in iframe / embed — AFTER all hooks to respect Rules of Hooks
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/embed")) {
    return null;
  }

  const collapse = () => {
    setCollapsed(true);
    try { localStorage.setItem(COLLAPSED_KEY, "1"); } catch { /* ignore */ }
  };
  const expand = () => {
    setCollapsed(false);
    try { localStorage.removeItem(COLLAPSED_KEY); } catch { /* ignore */ }
  };

  const goTo = (route) => {
    if (route.startsWith("http")) {
      window.open(route, "_blank", "noopener");
    } else {
      navigate(route);
    }
  };

  if (collapsed) {
    return (
      <button
        onClick={expand}
        data-testid="federation-clerk-dot"
        aria-label="Open Federation Clerk · Rex"
        className="fixed bottom-[78px] left-[14px] z-[58] rounded-full transition-transform hover:scale-110 active:scale-95 nx-clerk-bob"
        style={{
          width: 52, height: 52, padding: 0, overflow: "hidden",
          border: "2px solid var(--cyan)",
          boxShadow: "0 0 18px rgba(0,240,255,0.7), 0 6px 18px rgba(0,0,0,0.55)",
          background: "rgba(7,4,28,0.85)",
        }}
      >
        <img src={REX_IMG} alt={REX_ALT} className="w-full h-full object-cover" />
      </button>
    );
  }

  return (
    <div
      data-testid="federation-clerk"
      className="fixed bottom-[78px] left-[14px] z-[58] pointer-events-auto nx-clerk-bob"
      style={{ width: "min(340px, calc(100vw - 28px))" }}
    >
      <div
        className="rounded-2xl p-4"
        style={{
          background: "rgba(7, 4, 28, 0.86)",
          backdropFilter: "blur(14px)",
          border: "1px solid var(--cyan)",
          boxShadow: "0 0 36px rgba(0,240,255,0.45), 0 12px 36px rgba(0,0,0,0.55)",
        }}
      >
        {/* Header */}
        <div className="flex items-start gap-3">
          <img
            src={REX_IMG} alt={REX_ALT}
            className="w-12 h-12 rounded-full object-cover shrink-0"
            style={{ border: "1.5px solid var(--cyan)", boxShadow: "0 0 12px var(--cyan)" }}
          />
          <div className="min-w-0 flex-1">
            <div className="font-display text-[15px] text-glow-cyan leading-tight">
              Rex · The Greeter
            </div>
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--text-3)] mt-0.5">
              Federation front door · always on
            </div>
          </div>
          <button
            type="button"
            onClick={collapse}
            data-testid="federation-clerk-collapse"
            aria-label="Minimize"
            className="font-mono text-[10px] tracking-[0.22em] px-1.5 py-0.5 border border-[var(--chrome)] text-[var(--text-3)] hover:text-glow-cyan transition-colors shrink-0"
          >
            ⤓
          </button>
        </div>

        {/* Speech bubble */}
        <div className="mt-3 text-[12.5px] leading-relaxed text-[var(--text)]"
             style={{ textShadow: "0 1px 3px rgba(0,0,0,0.55)" }}>
          Welcome to the Cathedral, friend. <span style={{ color: "var(--cyan)" }}>10 minds</span> live here.
          What can we think about together?
        </div>

        {/* Quick actions */}
        <div className="mt-3 grid grid-cols-1 gap-1.5">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.route}
              onClick={() => goTo(a.route)}
              data-testid={a.test}
              className="text-left font-mono text-[11px] uppercase tracking-[0.16em] px-3 py-2 rounded-md transition-colors hover:bg-[rgba(0,240,255,0.10)]"
              style={{
                border: "1px solid rgba(0,240,255,0.32)",
                color: "var(--text)",
                background: "rgba(0,240,255,0.04)",
                minHeight: "36px",
              }}
            >
              {a.label}
            </button>
          ))}
        </div>

        {/* Satellite redirect */}
        <button
          onClick={() => setShowSatellites((v) => !v)}
          data-testid="federation-clerk-satellites-toggle"
          className="mt-3 w-full text-center font-mono text-[10px] uppercase tracking-[0.22em] py-1.5 text-[var(--text-3)] hover:text-glow-magenta transition-colors"
          style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
        >
          {showSatellites ? "▾ hide" : "▸ looking for something else?"}
        </button>
        {showSatellites && (
          <div className="grid grid-cols-2 gap-1.5 mt-2">
            {SATELLITE_LINKS.map((s) => (
              <button
                key={s.route}
                onClick={() => goTo(s.route)}
                data-testid={`clerk-sat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="text-left font-mono text-[10px] px-2 py-1.5 rounded transition-colors hover:bg-[rgba(255,0,255,0.10)]"
                style={{
                  border: "1px solid rgba(255,0,255,0.28)",
                  color: "var(--text-2)",
                  background: "rgba(255,0,255,0.04)",
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
